import { Keyboard } from '@/input/Keyboard';
import { VirtualJoystick } from '@/input/VirtualJoystick';
import { Gamepad } from '@/input/Gamepad';
import { Tilt } from '@/input/Tilt';
import { ItemButton, type ItemButtonState } from '@/input/ItemButton';

/**
 * InputManager.ts — unifies every input source into one tidy frame.
 *
 * Touch joystick, keyboard and gamepad are read each tick and merged (the source
 * pushing hardest on an axis wins). `steer` here is RAW screen-space (+ = right);
 * the player layer applies the §6.2 flip before feeding the car.
 *
 * The use-item control (M12) is a FIRE EDGE, not a held state: keyboard/gamepad
 * holds are converted to one-shot "just pressed" pulses here (so holding the key
 * fires exactly once), merged with the touch item button's taps.
 */
export interface InputFrame {
  throttle: number;
  steer: number; // screen-space (+ = right)
  useItem: boolean; // fire the held item this frame (edge)
  active: boolean;
}

/** Of the candidate axis values, keep the one with the largest magnitude. */
function strongest(...values: number[]): number {
  let best = 0;
  for (const v of values) if (Math.abs(v) > Math.abs(best)) best = v;
  return best;
}

export class InputManager {
  private keyboard = new Keyboard();
  private joystick: VirtualJoystick;
  private gamepad = new Gamepad();
  private tilt = new Tilt();
  private itemButton: ItemButton;

  /** Previous-frame held state of the use-item control, for edge detection. */
  private prevUseHeld = false;
  /** Previous-frame held state of the pause control (its own edge detector —
   *  pause is polled every RENDER frame so it works while the sim is paused). */
  private prevPause = false;

  constructor(container: HTMLElement) {
    this.joystick = new VirtualJoystick(container);
    this.itemButton = new ItemButton(container);
  }

  read(): InputFrame {
    const k = this.keyboard.read();
    const j = this.joystick.read();
    const g = this.gamepad.read();
    const ti = this.tilt.read();
    const tap = this.itemButton.read();

    // Keyboard/gamepad: held → one-shot rising edge. Touch button: already a tap.
    const held = k.useItem || g.useItem;
    const useItem = (held && !this.prevUseHeld) || tap;
    this.prevUseHeld = held;

    return {
      throttle: strongest(j.active ? j.y : 0, g.throttle, k.throttle),
      // Tilt (when active) joins the steer merge: a real tilt dominates while the
      // resting joystick x ≈ 0, and if tilt drops out the joystick still steers.
      steer: strongest(j.active ? j.x : 0, g.steer, k.steer, ti.active ? ti.steer : 0),
      useItem,
      active: j.active || g.active || k.active || (ti.active && ti.steer !== 0),
    };
  }

  /** M112 (demo race): a NON-DESTRUCTIVE sample for the tutorial director.
   *  The tutorial freeze stops Game.update's read(), so the director peeks
   *  axis LEVELS here without consuming the use-item edge (`prevUseHeld`) or
   *  the touch button's one-shot tap latch — both stay armed for the race's
   *  own read() the moment the freeze lifts. */
  peek(): { throttle: number; steer: number; itemHeld: boolean; itemQueued: boolean } {
    const k = this.keyboard.read();
    const j = this.joystick.read();
    const g = this.gamepad.read();
    const ti = this.tilt.read();
    return {
      throttle: strongest(j.active ? j.y : 0, g.throttle, k.throttle),
      steer: strongest(j.active ? j.x : 0, g.steer, k.steer, ti.active ? ti.steer : 0),
      itemHeld: k.useItem || g.useItem,
      itemQueued: this.itemButton.peekPressed(),
    };
  }

  /** M112: forget the held-key state so the NEXT read() treats a still-held
   *  use-item key as a fresh press. The demo race freezes the sim while the
   *  FIRE popup waits for the press; a key still down from before the freeze
   *  would otherwise swallow the resumed frame's rising edge and the taught
   *  boost would never fire. */
  rearmItemEdge(): void {
    this.prevUseHeld = false;
  }

  /** M112: coach-pulse the item button (the FIRE YOUR BOOST teaching aid).
   *  M115: the cue has LEVELS — see ItemButton.setHighlight. */
  setItemHighlight(level: 0 | 1 | 2): void {
    this.itemButton.setHighlight(level);
  }

  /** One-shot pause press (Escape / P / gamepad Start). Poll once per rendered
   *  frame — unlike read(), this keeps working while the race sim is paused. */
  pauseEdge(): boolean {
    const held = this.keyboard.read().pause || this.gamepad.read().pause;
    const edge = held && !this.prevPause;
    this.prevPause = held;
    return edge;
  }

  /** Racing controls on/off: menus hide the joystick + item button and ignore
   *  their touches (the keyboard/gamepad axes are simply not read in menus). */
  setEnabled(enabled: boolean): void {
    this.joystick.setEnabled(enabled);
    this.itemButton.setVisible(enabled);
    if (enabled) this.tilt.calibrate(); // fresh tilt centre when a race's controls come on
  }

  /** Left-handed touch layout (M31): joystick to the right, item button to the
   *  left. Driven by the settings screen via Game.applySettings. */
  setLefty(lefty: boolean): void {
    this.joystick.setSide(lefty ? 'right' : 'left');
    this.itemButton.setLefty(lefty);
  }

  /** M115: swap the touch feel for the BIG KID kids difficulty — a much bigger
   *  pad and a much gentler steering curve, sized for a small child's index
   *  finger on an iPad. Off Big Kid this is the adult profile, unchanged. */
  setBigKid(on: boolean): void {
    this.joystick.setBigKid(on);
  }

  /** Touch steering-sensitivity multiplier from the Settings slider (≈0.5..1.5). */
  setSteerSensitivity(mult: number): void {
    this.joystick.setSteerSensitivity(mult);
  }

  /** Pick the touch steering scheme. 'tilt' enables the gyro source (dormant until
   *  events arrive); 'joystick' disables it. The joystick is always present — it
   *  carries the gas and is the steering fallback whenever tilt is inactive. */
  setControlScheme(scheme: 'joystick' | 'tilt'): void {
    this.tilt.setEnabled(scheme === 'tilt');
  }

  /** Trigger the iOS motion-permission prompt (must come from a user gesture — the
   *  Settings "enable tilt" tap). Recalibrates the centre on success.
   *  PARKED (M115, owner): tilt was removed as a player option, so NOTHING calls
   *  this any more — which is exactly the point: the game never asks a player for
   *  motion access. Kept beside input/Tilt.ts so the idea can come back whole. */
  async requestTiltPermission(): Promise<boolean> {
    const ok = await this.tilt.requestPermission();
    if (ok) this.tilt.calibrate();
    return ok;
  }

  /** Push the player's item-slot state to the on-screen button each frame. */
  updateItemButton(state: ItemButtonState): void {
    this.itemButton.setState(state);
  }

  /** Shake the item button (a magnet/kick fire was refused — too far). */
  denyItem(): void {
    this.itemButton.deny();
  }

  dispose(): void {
    this.keyboard.dispose();
    this.joystick.dispose();
    this.tilt.dispose();
    this.itemButton.dispose();
  }
}
